<script setup lang="ts">
/** The terms and what they mean. */
import { useI18n } from 'vue-i18n'

import InspectorField from '@/sections/InspectorField.vue'
import { newGlossaryEntry, type GlossaryEntry, type GlossarySection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: GlossarySection }>()
const emit = defineEmits<{ change: [section: GlossarySection] }>()

function set(changes: Partial<GlossarySection>): void {
  emit('change', { ...props.section, ...changes })
}

function setEntry(id: string, changes: Partial<GlossaryEntry>): void {
  set({
    entries: props.section.entries.map((one) => (one.id === id ? { ...one, ...changes } : one)),
  })
}

function add(): void {
  set({ entries: [...props.section.entries, newGlossaryEntry()] })
}

function remove(id: string): void {
  set({ entries: props.section.entries.filter((one) => one.id !== id) })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.glossary.title')">
      <input
        class="b-input"
        :value="section.title"
        @input="set({ title: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <div class="entries">
      <article v-for="entry in section.entries" :key="entry.id" class="entry">
        <header class="entry__head">
          <input
            class="b-input entry__term"
            :value="entry.term"
            :placeholder="t('inspector.glossary.termPlaceholder')"
            @input="setEntry(entry.id, { term: ($event.target as HTMLInputElement).value })"
          />
          <button
            type="button"
            class="b-icon-btn"
            :aria-label="t('inspector.glossary.removeEntry')"
            @click="remove(entry.id)"
          >
            &times;
          </button>
        </header>
        <textarea
          class="b-textarea"
          rows="2"
          :value="entry.definition"
          :placeholder="t('inspector.glossary.definitionPlaceholder')"
          @input="setEntry(entry.id, { definition: ($event.target as HTMLTextAreaElement).value })"
        />
      </article>
    </div>

    <button type="button" class="b-btn b-btn--outline" @click="add">
      {{ t('inspector.glossary.addEntry') }}
    </button>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.entries {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.entry {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-5);
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.entry__head {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
}

.entry__term {
  font-weight: 500;
}
</style>
